import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {Modal} from '@udemy/react-dialog-components';
import {Checkbox, ToggleInputBlockFormGroup} from '@udemy/react-form-components';
import {TakeOverWizard} from '@udemy/react-structure-components';
import {udSentry} from '@udemy/sentry';
import {safelySetInnerHTML} from '@udemy/shared-utils';

import {
    QuizItemResponseStatus,
    usePopQuizAttemptCompleteMutation,
    usePopQuizItemResponseSubmitMutation,
} from 'src/generated/graphql';

import styles from '../pop-quiz-modal.module.less';
import {
    PopQuizRelatedLectureLinkPresented,
    PopQuizRelatedLectureLinkSelected,
    QuizCompleted,
} from './events';
import {PopQuizLectureLink} from './pop-quiz-lecture-link.react-component';
import {PopQuizOption} from './pop-quiz-option.react-component';
import {PopQuizItem, PopQuizRelatedLecture, QuizCandidate} from './types';
import {publishPopQuizEvent, renderUdemyLogo, toEventId} from './utils';

interface PopQuizQuizPhaseProps {
    isOpen: boolean;
    quizData: QuizCandidate;
    questions: PopQuizItem[];
    attemptId: string | null;
    stopShowing: boolean;
    setStopShowing: (v: boolean) => void;
    onComplete: (correctCount: number) => void;
    onExit: () => void;
    popQuizV2ExperienceEnabled: boolean;
}

export function PopQuizQuizPhase({
    isOpen,
    quizData,
    questions,
    attemptId,
    stopShowing,
    setStopShowing,
    onComplete,
    onExit,
    popQuizV2ExperienceEnabled,
}: PopQuizQuizPhaseProps) {
    const {gettext, interpolate} = useI18n();

    const [selectedAnswers, setSelectedAnswers] = useState<Set<string>>(new Set());
    const [checked, setChecked] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    // correctness verdict added to support related lectures for multiple-select questions
    const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);

    const questionStartTimeRef = useRef<number>(Date.now());

    const submitResponse = usePopQuizItemResponseSubmitMutation();
    const completeAttempt = usePopQuizAttemptCompleteMutation();

    const onSelectAnswer = useCallback(
        (optionId: string) => {
            if (checked) {
                return;
            }
            const q = questions[currentIndex];
            if (!q) {
                return;
            }
            if (q.isMultiSelect) {
                setSelectedAnswers((prev) => {
                    const next = new Set(prev);
                    if (next.has(optionId)) {
                        next.delete(optionId);
                    } else {
                        next.add(optionId);
                    }
                    return next;
                });
            } else {
                setSelectedAnswers(new Set([optionId]));
            }
        },
        [checked, questions, currentIndex],
    );

    const onCheckAnswer = useCallback(async () => {
        const q = questions[currentIndex];
        if (!q) {
            return;
        }
        const elapsed = Math.max(1, Math.round((Date.now() - questionStartTimeRef.current) / 1000));
        let answeredCorrectly: boolean;
        if (attemptId) {
            try {
                const result = await submitResponse.mutateAsync({
                    itemResponse: {
                        attemptId,
                        quizId: String(quizData.quizId),
                        itemId: q.id,
                        selectedOptionIds: Array.from(selectedAnswers),
                        durationInSeconds: elapsed,
                    },
                });
                answeredCorrectly =
                    result?.quizItemResponseSubmit?.status === QuizItemResponseStatus.Correct;
            } catch (e) {
                // Submission failed — fall back to local correctness so the quiz still works
                udSentry.captureException(e);
                const selected = Array.from(selectedAnswers).sort();
                const correct = [...q.correctOptionIds].sort();
                answeredCorrectly =
                    selected.length === correct.length &&
                    selected.every((val, i) => val === correct[i]);
            }
        } else {
            const selected = Array.from(selectedAnswers).sort();
            const correct = [...q.correctOptionIds].sort();
            answeredCorrectly =
                selected.length === correct.length &&
                selected.every((val, i) => val === correct[i]);
        }
        setChecked(true);
        setLastAnswerCorrect(answeredCorrectly);
        if (answeredCorrectly) {
            setCorrectCount((c) => c + 1);
        }
    }, [questions, currentIndex, attemptId, quizData.quizId, selectedAnswers, submitResponse]);

    // Completes the server-side attempt (non-fatal) and fires QuizCompleted.
    // Shared by both the post-answer and skip paths on the final question.
    const completeQuiz = useCallback(async () => {
        if (attemptId) {
            try {
                await completeAttempt.mutateAsync({
                    quizAttemptId: attemptId,
                    quizId: String(quizData.quizId),
                });
            } catch (e) {
                // Non-fatal: show results regardless of completion call success
                udSentry.captureException(e);
            }
        }
        const percentCorrect = questions.length > 0 ? correctCount / questions.length : 0;
        publishPopQuizEvent(
            new QuizCompleted(
                {id: quizData.quizId, numberOfQuestions: questions.length},
                {id: quizData.courseId},
                Number(attemptId),
                percentCorrect,
            ),
            quizData,
        );
        onComplete(correctCount);
    }, [attemptId, completeAttempt, correctCount, quizData, questions.length, onComplete]);

    const onSelectLectureLink = useCallback(
        (lecture: PopQuizRelatedLecture) => {
            const lectureId = toEventId(lecture.id);
            if (lectureId === null) {
                return;
            }
            publishPopQuizEvent(
                new PopQuizRelatedLectureLinkSelected(
                    {id: quizData.quizId, numberOfQuestions: questions.length},
                    {id: quizData.courseId},
                    {id: lectureId, type: lecture.type},
                ),
                quizData,
            );
        },
        [quizData, questions.length],
    );

    const advanceQuestion = useCallback(() => {
        questionStartTimeRef.current = Date.now();
        setCurrentIndex((i) => i + 1);
        setSelectedAnswers(new Set());
        setChecked(false);
        setLastAnswerCorrect(null);
    }, []);

    // Shared handler for "Next question" (post-answer) and "Skip question".
    // Completes the quiz on the final question; otherwise advances to the next.
    const onAdvance = useCallback(async () => {
        if (currentIndex >= questions.length - 1) {
            await completeQuiz();
        } else {
            advanceQuestion();
        }
    }, [advanceQuestion, completeQuiz, currentIndex, questions.length]);

    const currentQuestion = questions[currentIndex];

    // The lecture link only makes sense as a "go review this" nudge — showing it after a fully
    // correct answer reads oddly, so it's gated on the real verdict from onCheckAnswer being
    // incorrect (not a heuristic re-derived from selectedAnswers, which mis-grades a partial
    // multi-select pick as "not incorrect").
    const presentedLecture =
        popQuizV2ExperienceEnabled && checked && lastAnswerCorrect === false
            ? currentQuestion?.relatedLectures[0]
            : undefined;

    // Fires once per reveal — presentedLecture only changes when a new lecture link is shown
    // (not on every unrelated re-render), giving PopQuizRelatedLectureLinkSelected a denominator.
    useEffect(() => {
        if (!presentedLecture) {
            return;
        }
        const lectureId = toEventId(presentedLecture.id);
        if (lectureId === null) {
            return;
        }
        publishPopQuizEvent(
            new PopQuizRelatedLectureLinkPresented(
                {id: quizData.quizId, numberOfQuestions: questions.length},
                {id: quizData.courseId},
                {id: lectureId, type: presentedLecture.type},
            ),
            quizData,
        );
    }, [presentedLecture, quizData, questions.length]);

    if (!currentQuestion) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onExit}
            title=""
            renderTitle={() => [
                'pop-quiz-modal-label',
                <span key="t" id="pop-quiz-modal-label" className="ud-sr-only">
                    {gettext('Pop quiz')}
                </span>,
            ]}
            className={styles['quiz-modal']}
            requireExplicitAction={true}
        >
            <TakeOverWizard
                fullScreenHeight={false}
                headerProps={{
                    renderLogo: renderUdemyLogo,
                    centerChildren: interpolate(
                        gettext('Question %(current)s of %(total)s'),
                        {current: currentIndex + 1, total: questions.length},
                        true,
                    ),
                    rightChildren: (
                        <Button udStyle="ghost" size="medium" onClick={onExit}>
                            {gettext('Exit')}
                        </Button>
                    ),
                }}
                footerProps={{
                    leftChildren: (
                        <Checkbox
                            checked={stopShowing}
                            onChange={(e) => setStopShowing(e.target.checked)}
                        >
                            {gettext('Stop showing me this')}
                        </Checkbox>
                    ),
                    rightChildren: (
                        <div className={styles['footer-actions']}>
                            <Button udStyle="ghost" size="medium" onClick={onAdvance}>
                                {gettext('Skip question')}
                            </Button>
                            <Button
                                udStyle="primary"
                                size="medium"
                                disabled={!checked && selectedAnswers.size === 0}
                                onClick={checked ? onAdvance : onCheckAnswer}
                            >
                                {checked
                                    ? currentIndex < questions.length - 1
                                        ? gettext('Next question')
                                        : gettext('See results')
                                    : gettext('Check answer')}
                            </Button>
                        </div>
                    ),
                }}
            >
                <div className={styles['quiz-content']}>
                    <div
                        {...safelySetInnerHTML({
                            descriptionOfCaller: 'pop-quiz:question-text',
                            html: currentQuestion.text,
                        })}
                    />

                    {currentQuestion.isMultiSelect && (
                        <p className={`ud-text-xs ${styles['multi-select-hint']}`}>
                            {gettext('Select all that apply')}
                        </p>
                    )}

                    <ToggleInputBlockFormGroup
                        label={
                            currentQuestion.isMultiSelect
                                ? gettext('Select your answers')
                                : gettext('Select your answer')
                        }
                        labelProps={{className: 'ud-sr-only'}}
                        className={styles.answers}
                    >
                        {currentQuestion.options.map((option) => (
                            <PopQuizOption
                                key={option.id}
                                option={option}
                                questionId={currentQuestion.id}
                                isMultiSelect={currentQuestion.isMultiSelect}
                                isSelected={selectedAnswers.has(option.id)}
                                isChecked={checked}
                                correctOptionIds={currentQuestion.correctOptionIds}
                                onSelect={onSelectAnswer}
                                questionExplanation={currentQuestion.explanation}
                                popQuizV2ExperienceEnabled={popQuizV2ExperienceEnabled}
                            />
                        ))}
                    </ToggleInputBlockFormGroup>

                    {presentedLecture && (
                        <PopQuizLectureLink
                            lecture={presentedLecture}
                            onSelect={() => onSelectLectureLink(presentedLecture)}
                        />
                    )}
                </div>
            </TakeOverWizard>
        </Modal>
    );
}
